/**
 * API Service - Handles JSON/OpenAPI communication with the backend
 * This replaces the protobuf-based communication with simpler JSON
 */

import { API_CONFIG } from '../config/api.js';
import { IS_STANDALONE } from '../config/runtimeProfile.js';
import { loggers } from '../utils/logger.js';

const log = loggers.api;
const authLog = loggers.auth;

class ApiService {
  constructor() {
    this.baseUrl = API_CONFIG.BACKEND_URL;
    // All endpoints now consolidated under /api prefix
    this.v2Prefix = '/api/v2';  // New OpenAPI endpoints
    this.getAccessToken = null; // Will be set from App.jsx
    this.onAuthError = null; // Callback for auth failures
    // Map of in-flight requests for deduping identical concurrent calls
    this.inflight = new Map();
    this.tokenProviderVersion = 0;
    this.tokenProviderListeners = new Set();
    this.imageBaseUrl = null;
    this.imageBaseUrlVersion = 0;
    this.imageBaseUrlListeners = new Set();
    // Cache for authorized media blob URLs to avoid re-fetching unchanged images
    // Structure: Map<rawUrl, { blobUrl, timestamp }>
    this.mediaUrlCache = new Map();
    this.mediaUrlCacheTTL = 5 * 60 * 1000; // 5 minutes TTL

    // Negative cache for failed media URL requests to prevent retry storms
    // Structure: Map<cacheKey, { status, timestamp }>
    this.mediaUrlFailureCache = new Map();
    this.mediaUrlFailureCacheTTL = {
      403: 5 * 60 * 1000,  // 5 minutes for unauthorized (likely won't change)
      404: 60 * 1000,      // 1 minute for not found (might be generated soon)
      default: 30 * 1000,  // 30 seconds for other errors
    };
    this.mediaUrlFailureCacheMaxSize = 1000; // Limit cache size
  }

  /**
   * Small helper to sleep for a given number of milliseconds.
   */
  sleep(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  /**
   * Parse Retry-After header value (seconds or HTTP-date) into milliseconds.
   */
  parseRetryAfter(retryAfterValue) {
    if (!retryAfterValue) return null;
    const s = parseInt(retryAfterValue, 10);
    if (!Number.isNaN(s)) {
      return Math.max(0, s * 1000);
    }
    const dateMs = Date.parse(retryAfterValue);
    if (!Number.isNaN(dateMs)) {
      return Math.max(0, dateMs - Date.now());
    }
    return null;
  }

  async _parseErrorPayload(response) {
    const jsonSource = typeof response.clone === 'function' ? response.clone() : response;
    try {
      return await jsonSource.json();
    } catch {
      // Fall through to a text body. Real Response objects use the clone above
      // so failed JSON parsing does not consume the original body.
    }

    if (typeof response.text !== 'function') {
      return null;
    }

    try {
      const text = await response.text();
      const trimmed = typeof text === 'string' ? text.trim() : '';
      return trimmed ? { message: trimmed } : null;
    } catch {
      return null;
    }
  }

  _buildHttpError(response, errPayload = null) {
    const detail = errPayload?.detail;
    const isStructuredDetail =
      detail && typeof detail === 'object' && !Array.isArray(detail) && typeof detail.code === 'string';
    const errMsg = isStructuredDetail
      ? (detail.message || `HTTP error! status: ${response.status}`)
      : (typeof detail === 'string' ? detail : null)
        || errPayload?.message
        || `HTTP error! status: ${response.status}`;
    const err = new Error(errMsg);
    err.status = response.status;
    if (isStructuredDetail) {
      err.code = detail.code;
      err.detailPayload = detail;
    }
    return err;
  }

  /**
   * Fetch JSON with in-flight deduplication and 429 Retry-After handling.
   * - Dedupes identical concurrent requests by method+url+body.
   * - On 429 (or 503 with Retry-After), waits per Retry-After or exponential backoff, then retries.
   */
  async _fetchJsonWithDedupe(url, options = {}, retryOptions = {}) {
    const method = (options.method || 'GET').toUpperCase();
    const bodyKey = options.body
      ? (typeof options.body === 'string' ? options.body : JSON.stringify(options.body))
      : '';
    const dedupeKey = `${method} ${url} ${bodyKey}`;

    if (this.inflight.has(dedupeKey)) {
      return this.inflight.get(dedupeKey);
    }

    const attemptFetch = async () => {
      const {
        maxAttempts = 3,
        baseDelayMs = 1000,
        jitterMs = 300,
      } = retryOptions || {};

      // Merge headers and attach Authorization if available
      const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
      if (!headers.Authorization && this.getAccessToken) {
        try {
          const token = await this.getAccessToken();
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (e) {
          // Check if this is an Auth0 error indicating expired/invalid refresh token
          if (e.error === 'login_required' ||
              e.error === 'invalid_grant' ||
              e.message?.includes('login_required') ||
              e.message?.includes('expired')) {
            authLog.error('Refresh token expired or invalid - triggering logout');
            if (this.onAuthError) {
              this.onAuthError(e);
            }
          }
          // Non-fatal; proceed unauthenticated
        }
      }

      let lastError = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
        try {
          const requestOptions = {
            ...options,
            method,
            headers,
          };
          if (!('credentials' in requestOptions)) {
            requestOptions.credentials = 'include';
          }

          const response = await fetch(url, requestOptions);

          if (response.status === 429 || (response.status === 503 && response.headers.has('retry-after'))) {
            // Rate limited or unavailable with guidance to retry
            const retryAfterHeader = response.headers.get('retry-after');
            const retryMsFromHeader = this.parseRetryAfter(retryAfterHeader);
            const backoffMs = retryMsFromHeader ?? (baseDelayMs * Math.pow(2, attempt - 1));
            const jitter = Math.floor(Math.random() * jitterMs);
            if (attempt < maxAttempts) {
              await this.sleep(backoffMs + jitter);
              continue;
            }
          }

          if (!response.ok) {
            // Try to parse response JSON error body but fall back to text
            const errPayload = await this._parseErrorPayload(response);

            const err = this._buildHttpError(response, errPayload);

            // Check for 401 Unauthorized - token expired or invalid
            if (response.status === 401 && this.onAuthError) {
              authLog.error('401 Unauthorized - token expired or invalid');
              this.onAuthError(new Error('Authentication token expired'));
            }

            throw err;
          }

          // Success
          return await response.json();
        } catch (err) {
          lastError = err;
          // On network errors or other transient failures, backoff and retry
          if (attempt < maxAttempts) {
            const backoffMs = baseDelayMs * Math.pow(2, attempt - 1);
            const jitter = Math.floor(Math.random() * jitterMs);
            await this.sleep(backoffMs + jitter);
            continue;
          }
          throw err;
        }
      }

      // If all attempts failed, throw the last error
      throw lastError || new Error('Request failed');
    };

    const promise = attemptFetch()
      .finally(() => {
        // Ensure we clear the inflight entry regardless of outcome
        this.inflight.delete(dedupeKey);
      });

    this.inflight.set(dedupeKey, promise);
    return promise;
  }

  /**
   * Encode a value for safe use in URL path segments.
   * @param {string|number} segment - Raw segment value
   * @returns {string} URL-safe segment
   */
  encodePathSegment(segment) {
    if (segment === undefined || segment === null) {
      return '';
    }
    return encodeURIComponent(String(segment));
  }

  isAbsoluteUrl(url) {
    return /^https?:\/\//i.test(url);
  }

  isSpecialScheme(url) {
    return /^(?:data:|blob:|file:)/i.test(url || '');
  }

  stripSignedImageParams(rawUrl) {
    if (!rawUrl || !String(rawUrl).includes('?')) {
      return rawUrl;
    }
    const [base, query] = String(rawUrl).split('?');
    if (!query) {
      return rawUrl;
    }
    const params = new URLSearchParams(query);
    params.delete('sig');
    params.delete('exp');
    const next = params.toString();
    return next ? `${base}?${next}` : base;
  }

  hasSignedImageParams(rawUrl) {
    if (!rawUrl || !String(rawUrl).includes('?')) {
      return false;
    }
    const [, query] = String(rawUrl).split('?');
    if (!query) {
      return false;
    }
    const params = new URLSearchParams(query);
    return params.has('sig') && params.has('exp');
  }

  buildAbsoluteUrl(rawUrl) {
    if (!rawUrl || this.isAbsoluteUrl(rawUrl) || this.isSpecialScheme(rawUrl)) {
      return rawUrl || null;
    }

    if (rawUrl.startsWith('//')) {
      const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
      return `${protocol}${rawUrl}`;
    }

    // On native, window.location.origin is https://localhost (Capacitor WebView)
    // which has no backend — always use the configured backend URL.
    const nativeBackend = (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.())
      ? (API_CONFIG.BACKEND_URL || 'http://localhost:8090')
      : '';
    const base =
      this.baseUrl || nativeBackend ||
      (typeof window !== 'undefined' ? window.location.origin : '');

    if (!base) {
      return rawUrl;
    }

    try {
      const absolute = new URL(rawUrl, base);
      return absolute.toString();
    } catch (error) {
      log.warn('Failed to resolve absolute URL:', rawUrl, error);
      if (rawUrl.startsWith('/')) {
        return `${base.replace(/\/$/, '')}${rawUrl}`;
      }
      return rawUrl;
    }
  }

  normalizeImageBaseUrl(rawUrl) {
    if (!rawUrl) {
      return null;
    }
    let value = String(rawUrl).trim();
    if (!value) {
      return null;
    }
    if (value.endsWith('/')) {
      value = value.slice(0, -1);
    }
    return value;
  }

  setImageBaseUrl(rawUrl) {
    const next = this.normalizeImageBaseUrl(rawUrl);
    if (next === this.imageBaseUrl) {
      return this.imageBaseUrl;
    }
    this.imageBaseUrl = next;
    this.imageBaseUrlVersion += 1;
    this.notifyImageBaseUrlListeners();
    return this.imageBaseUrl;
  }

  getImageBaseUrl() {
    return this.imageBaseUrl;
  }

  subscribeImageBaseUrl(listener) {
    if (typeof listener !== 'function') {
      return () => {};
    }
    this.imageBaseUrlListeners.add(listener);
    return () => {
      this.imageBaseUrlListeners.delete(listener);
    };
  }

  notifyImageBaseUrlListeners() {
    for (const listener of this.imageBaseUrlListeners) {
      try {
        listener(this.imageBaseUrlVersion, this.imageBaseUrl);
      } catch (error) {
        log.warn('Image base URL listener error:', error);
      }
    }
  }

  extractImageStoragePath(rawUrl) {
    if (!rawUrl) {
      return null;
    }

    let candidate = String(rawUrl).trim();
    if (!candidate) {
      return null;
    }

    try {
      const absolute = this.buildAbsoluteUrl(candidate);
      const parsed = new URL(absolute);
      candidate = parsed.pathname;
    } catch {
      candidate = candidate.split('?')[0];
    }

    if (candidate.startsWith('/api/images/')) {
      return candidate.replace('/api/images/', '');
    }
    if (candidate.startsWith('api/images/')) {
      return candidate.replace('api/images/', '');
    }
    if (candidate.startsWith('/media/images/')) {
      return candidate.replace('/media/images/', '');
    }
    if (candidate.startsWith('media/images/')) {
      return candidate.replace('media/images/', '');
    }

    return null;
  }

  inferCampaignIdFromStoragePath(storagePath) {
    if (!storagePath) {
      return null;
    }

    const firstSegment = String(storagePath).split('/').filter(Boolean)[0] || null;
    if (!firstSegment) {
      return null;
    }

    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(firstSegment)) {
      return firstSegment;
    }
    if (/^\w*campaign_\d+$/i.test(firstSegment)) {
      return firstSegment;
    }

    return null;
  }

  resolveImageUrl(rawUrl) {
    if (!rawUrl || this.isAbsoluteUrl(rawUrl) || this.isSpecialScheme(rawUrl)) {
      return rawUrl || null;
    }
    const rawStr = String(rawUrl);
    const imageBaseUrl = this.imageBaseUrl;
    // Only strip signature params when redirecting to CDN (imageBaseUrl set)
    // Keep them for local proxy URLs that require HMAC signatures
    if (imageBaseUrl) {
      if (rawStr.startsWith('/api/images/') && this.hasSignedImageParams(rawStr)) {
        // Preserve signed backend URLs - CDN cookie scope may not cover UUID campaign paths.
        return this.buildAbsoluteUrl(rawStr);
      }

      const cleaned = this.stripSignedImageParams(rawStr);
      if (cleaned.startsWith('/api/images/')) {
        const rest = cleaned.slice('/api/images/'.length);
        return `${imageBaseUrl}/${rest.replace(/^\/+/, '')}`;
      }
      if (cleaned.startsWith('/media/images/') || cleaned.startsWith('media/images/')) {
        const normalized = cleaned.startsWith('/') ? cleaned : `/${cleaned}`;
        return `${imageBaseUrl}${normalized}`;
      }
    }
    // No CDN - preserve signature params for local proxy auth
    if (rawStr.startsWith('/media/images/') || rawStr.startsWith('media/images/')) {
      // Strip the /media/images/ or media/images/ prefix and route through /api/images/
      const stripped = rawStr.replace(/^\/?media\/images\//, '');
      return this.buildAbsoluteUrl(`/api/images/${stripped}`);
    }
    return this.buildAbsoluteUrl(rawStr);
  }

  /**
   * Check if a URL is in the failure cache (recently failed with 403/404/etc)
   * @private
   */
  _isMediaUrlCachedAsFailure(cacheKey) {
    const cached = this.mediaUrlFailureCache.get(cacheKey);
    if (!cached) return null;

    const ttl = this.mediaUrlFailureCacheTTL[cached.status] || this.mediaUrlFailureCacheTTL.default;
    if (Date.now() - cached.timestamp < ttl) {
      return cached;
    }
    // Expired, remove from cache
    this.mediaUrlFailureCache.delete(cacheKey);
    return null;
  }

  /**
   * Cache a failed media URL request to prevent retry storms
   * @private
   */
  _cacheMediaUrlFailure(cacheKey, status) {
    // Prune cache if it gets too large (simple LRU-ish: just clear oldest half)
    if (this.mediaUrlFailureCache.size >= this.mediaUrlFailureCacheMaxSize) {
      const entries = Array.from(this.mediaUrlFailureCache.entries());
      entries.sort((a, b) => a[1].timestamp - b[1].timestamp);
      const toRemove = entries.slice(0, Math.floor(entries.length / 2));
      for (const [key] of toRemove) {
        this.mediaUrlFailureCache.delete(key);
      }
    }
    this.mediaUrlFailureCache.set(cacheKey, { status, timestamp: Date.now() });
  }

  async fetchMediaAsObjectUrl(absoluteUrl) {
    if (typeof window === 'undefined' || !window.URL || typeof window.URL.createObjectURL !== 'function') {
      return absoluteUrl;
    }

    // Use base URL (without query params) as cache key since signed URLs have changing signatures
    let cacheKey;
    try {
      const urlObj = new URL(absoluteUrl);
      cacheKey = urlObj.origin + urlObj.pathname;
    } catch {
      cacheKey = absoluteUrl;
    }

    // Check success cache first
    const cached = this.mediaUrlCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < this.mediaUrlCacheTTL) {
      return cached.blobUrl;
    }

    // Check failure cache - don't retry recently failed URLs
    const cachedFailure = this._isMediaUrlCachedAsFailure(cacheKey);
    if (cachedFailure) {
      const error = new Error(`Media request previously failed (status ${cachedFailure.status})`);
      error.status = cachedFailure.status;
      error.cached = true;
      throw error;
    }

    const headers = {};
    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }
      } catch (error) {
        log.warn('Failed to obtain auth token for media fetch:', error);
      }
    }

    const response = await fetch(absoluteUrl, {
      method: 'GET',
      headers,
      credentials: 'include',
    });

    if (response.status === 401 || response.status === 403) {
      // Don't trigger onAuthError for media requests - a stale signed URL or
      // expired media token shouldn't log out the user. Just fail the request.
      // Cache this failure to prevent retry storms
      this._cacheMediaUrlFailure(cacheKey, response.status);
      const error = new Error(`Media request unauthorized (status ${response.status})`);
      error.status = response.status;
      throw error;
    }

    if (!response.ok) {
      // Cache 404s and other failures to prevent retry storms
      this._cacheMediaUrlFailure(cacheKey, response.status);
      const error = new Error(`Media request failed: ${response.status}`);
      error.status = response.status;
      throw error;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    // Cache the result using the base URL as key
    // If there was an old cached blob URL for this cacheKey, revoke it
    if (cached?.blobUrl) {
      try {
        window.URL.revokeObjectURL(cached.blobUrl);
      } catch {
        // Ignore revocation errors
      }
    }
    this.mediaUrlCache.set(cacheKey, { blobUrl, timestamp: Date.now() });

    // Clear any failure cache entry now that we succeeded
    this.mediaUrlFailureCache.delete(cacheKey);

    return blobUrl;
  }

  /**
   * Invalidate cached media URL (e.g., when portrait is regenerated)
   * @param {string} rawUrl - The raw URL to invalidate
   */
  invalidateMediaCache(rawUrl) {
    if (!rawUrl) return;
    const absoluteUrl = this.buildAbsoluteUrl(rawUrl);

    // Use base URL (without query params) as cache key to match fetchMediaAsObjectUrl
    let cacheKey;
    try {
      const urlObj = new URL(absoluteUrl);
      cacheKey = urlObj.origin + urlObj.pathname;
    } catch {
      cacheKey = absoluteUrl;
    }

    const cached = this.mediaUrlCache.get(cacheKey);
    if (cached?.blobUrl) {
      try {
        window.URL.revokeObjectURL(cached.blobUrl);
      } catch {
        // Ignore revocation errors
      }
    }
    this.mediaUrlCache.delete(cacheKey);
    // Also clear any failure cache entry so the URL can be retried
    this.mediaUrlFailureCache.delete(cacheKey);
  }

  /**
   * Clear all expired entries from the media cache
   */
  cleanupMediaCache() {
    const now = Date.now();
    for (const [url, cached] of this.mediaUrlCache.entries()) {
      if (now - cached.timestamp >= this.mediaUrlCacheTTL) {
        try {
          window.URL.revokeObjectURL(cached.blobUrl);
        } catch {
          // Ignore revocation errors
        }
        this.mediaUrlCache.delete(url);
      }
    }
  }

  async buildAuthorizedMediaUrl(rawUrl, options = {}) {
    if (!rawUrl) {
      return null;
    }

    // Normalize media/images/ paths to /api/images/ before building absolute URL
    let normalizedUrl = rawUrl;
    const urlStr = String(rawUrl);
    if (urlStr.startsWith('/media/images/') || urlStr.startsWith('media/images/')) {
      const stripped = urlStr.replace(/^\/?media\/images\//, '');
      normalizedUrl = `/api/images/${stripped}`;
    }

    const absoluteUrl = this.buildAbsoluteUrl(normalizedUrl);
    if (!absoluteUrl) {
      return null;
    }

    const needsAuth = absoluteUrl.includes('/api/media/') || absoluteUrl.includes('/api/images/');
    if (!needsAuth) {
      return absoluteUrl;
    }

    if (absoluteUrl.startsWith('blob:') || absoluteUrl.startsWith('data:')) {
      return absoluteUrl;
    }

    // Signed /api/images URLs are explicitly designed to be used directly in <img>/<audio>.
    // Avoid blob-fetching them: cross-origin fetch (localhost:3000 -> localhost:8000) will
    // fail without CORS headers, but <img src> will still load fine.
    if (absoluteUrl.includes('/api/images/') && this.hasSignedImageParams(absoluteUrl)) {
      let finalUrl = absoluteUrl;
      // Append thumbnail size param if requested
      if (options.size) {
        try {
          const sizeUrl = new URL(absoluteUrl);
          sizeUrl.searchParams.set('size', String(options.size));
          finalUrl = sizeUrl.toString();
        } catch {
          // Ignore URL parse errors
        }
      }
      // Cache the direct URL under the same cache key used by fetchMediaAsObjectUrl
      // so callers relying on the cache don't endlessly re-resolve.
      try {
        const urlObj = new URL(finalUrl);
        const cacheKey = urlObj.origin + urlObj.pathname + (options.size ? `?size=${options.size}` : '');
        const cached = this.mediaUrlCache.get(cacheKey);
        if (cached?.blobUrl && cached.blobUrl.startsWith('blob:') && typeof window !== 'undefined') {
          try {
            window.URL.revokeObjectURL(cached.blobUrl);
          } catch {
            // Ignore revocation errors
          }
        }
        this.mediaUrlCache.set(cacheKey, { blobUrl: finalUrl, timestamp: Date.now() });
        this.mediaUrlFailureCache.delete(cacheKey);
      } catch {
        // Ignore caching issues; returning the URL is still correct.
      }
      return finalUrl;
    }

    // Append thumbnail size for non-signed image URLs
    let fetchUrl = absoluteUrl;
    if (options.size && absoluteUrl.includes('/api/images/')) {
      try {
        const sizeUrl = new URL(absoluteUrl);
        sizeUrl.searchParams.set('size', String(options.size));
        fetchUrl = sizeUrl.toString();
      } catch {
        // Ignore URL parse errors
      }
    }

    try {
      return await this.fetchMediaAsObjectUrl(fetchUrl);
    } catch (error) {
      if (options.allowAbsoluteFallback === false) {
        throw error;
      }
      log.warn('Failed to fetch authorized media blob:', error);
      return fetchUrl;
    }
  }

  /**
   * Set the token provider function from Auth0
   * @param {Function} tokenProvider - Function to get access token
   */
  setTokenProvider(tokenProvider) {
    authLog.debug('Setting Auth0 token provider in apiService');
    this.getAccessToken = tokenProvider;
    this.tokenProviderVersion += 1;
    this.notifyTokenProviderListeners();
  }

  /**
   * Set the auth error callback for automatic logout
   * @param {Function} callback - Function to call on auth errors
   */
  setAuthErrorCallback(callback) {
    authLog.debug('Setting auth error callback in apiService');
    this.onAuthError = callback;
  }

  subscribeTokenProvider(listener) {
    if (typeof listener !== 'function') {
      return () => {};
    }
    this.tokenProviderListeners.add(listener);
    return () => {
      this.tokenProviderListeners.delete(listener);
    };
  }

  notifyTokenProviderListeners() {
    for (const listener of this.tokenProviderListeners) {
      try {
        listener(this.tokenProviderVersion);
      } catch (error) {
        authLog.warn('Token provider listener error:', error);
      }
    }
  }

  /**
   * Fire-and-forget telemetry POST for optional-auth endpoints (e.g.
   * /api/playback/trace). Deliberately bypasses makeRequest/getAccessToken so a
   * non-critical telemetry batch can NEVER trigger the global auth-error handler
   * (logout) on an expired/refresh-failed token (Codex 3462631454). Uses
   * navigator.sendBeacon when available (survives unload, no auth header),
   * falling back to a no-auth keepalive fetch. The endpoint resolves identity
   * from body.user_id, so no Authorization header is needed — except in a
   * Standalone Install, whose backend requires the Local Player token on every
   * /api call: there it always uses the keepalive fetch, which the session
   * fetch wrapper authenticates.
   * @param {string} endpoint - API endpoint (without base URL)
   * @param {Object} body - JSON-serializable payload
   */
  postTelemetryBeacon(endpoint, body) {
    try {
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
      const payload = JSON.stringify(body);
      if (!IS_STANDALONE && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
        const blob = new Blob([payload], { type: 'application/json' });
        if (navigator.sendBeacon(url, blob)) return;
      }
      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      }).catch(() => {});
    } catch {
      /* telemetry must never throw */
    }
  }

  /**
   * Make a request to the backend API
   * @param {string} endpoint - The API endpoint (without base URL)
   * @param {Object} data - The request data
   * @param {string} method - HTTP method (default: 'POST')
   * @returns {Promise<Object>} The response data
   */
  async makeRequest(endpoint, data = null, method = 'POST', signal = null) {
    const headers = {
      'Content-Type': 'application/json',
    };
    
    // Try to get Auth0 token if provider is set
    if (this.getAccessToken) {
      try {
        // The token provider is now a wrapper function that returns the token
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        } else {
          authLog.warn('No access token received from Auth0');
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token:', error);

        // Check if this is an Auth0 error indicating expired/invalid refresh token
        if (error.error === 'login_required' ||
            error.error === 'invalid_grant' ||
            error.message?.includes('login_required') ||
            error.message?.includes('expired')) {
          authLog.error('Refresh token expired or invalid - triggering logout');
          if (this.onAuthError) {
            this.onAuthError(error);
          }
        }
      }
    }
    
    const options = {
      method,
      headers,
    };
    if (signal) {
      options.signal = signal;
    }

    if (method !== 'GET' && data) {
      options.body = JSON.stringify(data);
    }

    log.debug(`Request to ${endpoint}`);

    try {
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;
      const response = await fetch(url, options);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        log.error(`API Error Response (${response.status}):`, errorData);

        // Extract error message from various response formats
        let errorMessage;
        if (Array.isArray(errorData.detail)) {
          // Pydantic validation errors (422) - detail is an array
          errorMessage = errorData.detail.map(err => {
            const field = err.loc?.slice(1).join('.') || 'unknown';
            return `${field}: ${err.msg}`;
          }).join('; ');
        } else if (typeof errorData.detail === 'object' && errorData.detail !== null) {
          // Backend error object with error_message field
          errorMessage = errorData.detail.error_message ||
                        errorData.detail.message ||
                        JSON.stringify(errorData.detail);
        } else if (typeof errorData.detail === 'string') {
          // Simple string error
          errorMessage = errorData.detail;
        } else {
          // Fallback
          errorMessage = errorData.message || `HTTP error! status: ${response.status}`;
        }

        // Check for 401 Unauthorized - token expired or invalid
        if (response.status === 401) {
          authLog.error('401 Unauthorized - token expired or invalid');
          if (this.onAuthError) {
            this.onAuthError(new Error('Authentication token expired'));
          }
        }

        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      return responseData;
    } catch (error) {
      log.error(`Request failed:`, error);
      throw error;
    }
  }

  /**
   * Helper to parse structured data fields
   * @param {any} field - Field that might be JSON string or object
   * @returns {any} Parsed field
   */
  parseField(field) {
    if (!field) return '';
    
    // If it's already an object/array, return as-is
    if (typeof field === 'object' && field !== null) {
      return field;
    }
    
    // If it's a string that looks like JSON, try to parse it
    if (typeof field === 'string') {
      const trimmed = field.trim();
      if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        try {
          return JSON.parse(field);
        } catch (e) {
          log.warn('Failed to parse JSON field:', e);
          return field;
        }
      }
    }
    
    return field;
  }

  mapAudioPayload(payload, sessionId) {
    if (!payload || typeof payload !== 'object') {
      return null;
    }

    const fallbackSession = sessionId || payload.session_id || 'default-session';

    return {
      id: payload.id || `${fallbackSession}-${Date.now()}`,
      sessionId: payload.session_id || fallbackSession,
      url: payload.url,
      mimeType: payload.mime_type || 'audio/mpeg',
      durationSec: payload.duration_sec ?? null,
      createdAt: payload.created_at || new Date().toISOString(),
      sizeBytes: payload.size_bytes ?? null,
      provider: payload.provider || null,
    };
  }

  /**
   * Transform API response to match frontend expectations
   * @param {Object} response - Raw API response
   * @returns {Object} Transformed response
   */
  transformResponse(response) {
    if (!response.message?.structured_data) {
      return {
        response: 'No response received',
        structuredData: null,
        sessionId: response.campaign_id || response.session_id,
      };
    }
    
    const structuredData = response.message.structured_data;
    const sessionId =
      response.message?.campaign_id ||
      response.message?.session_id ||
      response.campaign_id ||
      response.session_id;
    const audioPayload = structuredData.audio ? this.mapAudioPayload(structuredData.audio, sessionId) : null;

    // Parse fields that might be JSON strings
    const transformedData = {
      narrative: this.parseField(structuredData.narrative),
      answer: structuredData.answer || 'No answer received',
      summary: structuredData.summary || '',
      observations: this.parseField(structuredData.observations) || [],
      turn_info: this.parseField(structuredData.turn_info),
      is_combat_active: structuredData.is_combat_active,
      interaction_type: structuredData.interaction_type || '',
      next_interaction_type: structuredData.next_interaction_type || '',
      streaming_answer: structuredData.streaming_answer || '',
      streaming_tool_events: this.parseField(structuredData.streaming_tool_events) || [],
      streamed: Boolean(structuredData.streamed),
      generated_image_url: structuredData.generated_image_url || '',
      generated_image_path: structuredData.generated_image_path || '',
      generated_image_prompt: structuredData.generated_image_prompt || '',
      generated_image_type: structuredData.generated_image_type || '',
      audio: audioPayload,
      original_data: structuredData,
    };

    return {
      response: transformedData.answer,
      structuredData: transformedData,
      sessionId,
    };
  }

  /**
   * Send a chat message
   * @param {string} message - The message to send
   * @param {string} sessionId - The session/campaign ID
   * @returns {Promise<Object>} Transformed response
   */
  async sendMessage(message, sessionId) {
    if (!sessionId) {
      throw new Error('sessionId is required to send a message');
    }
    log.debug('Sending chat message to session:', sessionId);
    
    const requestData = {
      message,
      session_id: sessionId,
      input_type: 'CHAT'
    };
    log.debug('Request data:', { input_type: requestData.input_type });
    
    const response = await this.makeRequest('/api/chat/compat', requestData);
    
    return this.transformResponse(response);
  }

  /**
   * Start a new campaign
   * @param {boolean} blank - Whether to create a blank campaign
   * @returns {Promise<Object>} Transformed response
   */
  async startNewCampaign(blank = false) {
    log.debug('Starting new campaign, blank:', blank);
    
    const response = await this.makeRequest('/api/campaigns/new', {
      blank
    });
    
    const transformed = this.transformResponse(response);
    // Ensure session_id is set for new campaigns
    transformed.sessionId =
      response.campaign_id ||
      response.session_id ||
      response.message?.campaign_id ||
      response.message?.session_id;
    
    return transformed;
  }

  /**
   * Send a new campaign request (compatibility method)
   * @returns {Promise<Object>} Transformed response
   */
  async sendNewCampaignRequest() {
    return this.startNewCampaign(false);
  }

  /**
   * Send a blank campaign request (compatibility method)
   * @returns {Promise<Object>} Transformed response
   */
  async sendBlankCampaignRequest() {
    return this.startNewCampaign(true);
  }

  /**
   * Add context to a campaign
   * @param {string} contextMessage - The context to add
   * @param {string} sessionId - The session/campaign ID
   * @returns {Promise<Object>} Success response
   */
  async addContext(contextMessage, sessionId) {
    if (!sessionId) {
      throw new Error('sessionId is required to add context');
    }
    log.debug('Adding context to campaign:', sessionId);
    
    const response = await this.makeRequest('/api/campaigns/add-context', {
      context: contextMessage,
      session_id: sessionId
    });
    
    return {
      success: response.success,
      message: response.message
    };
  }

  /**
   * Fetch recent images (compatibility with protobuf service)
   * @param {number} limit - Maximum number of images to fetch
   * @param {string} campaignId - Optional campaign ID filter
   * @returns {Promise<Array>} Array of image metadata
   */
  /**
   * Normalize a raw image record into a consistent shape with `url` and
   * `storagePath`.  Handles all known field variants across API responses,
   * socket events, and scene-state data so consumers never need priority
   * chains like `cdn_url || proxy_url || image_url || path`.
   */
  normalizeImageRecord(img) {
    if (!img) return img;
    // Already normalized
    if (img.url && img.storagePath !== undefined) return img;
    const rawUrl = img.url || img.cdn_url || img.proxy_url || img.image_url || img.path;
    const resolvedUrl = this.resolveImageUrl(rawUrl);
    let storagePath = img.storage_path || img.image_path || img.local_path || null;
    if (!storagePath && img.path) {
      const raw = String(img.path);
      if (raw.startsWith('/api/images/')) {
        storagePath = raw.replace('/api/images/', '').split('?')[0];
      } else if (raw.startsWith('media/images/')) {
        storagePath = raw;
      }
    }
    return { ...img, url: resolvedUrl, storagePath };
  }

  async fetchRecentImages(limit = 10, campaignId = null) {
    try {
      if (!campaignId) {
        log.warn('fetchRecentImages called without campaignId; returning empty list.');
        return [];
      }
      const params = new URLSearchParams({
        limit: String(limit),
        campaign_id: campaignId
      });
      const url = `${this.baseUrl}/api/images?${params}`;
      const data = await this._fetchJsonWithDedupe(url, { method: 'GET' }, { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 });
      const images = Array.isArray(data.images) ? data.images : [];
      return images.map((img) => this.normalizeImageRecord(img));
    } catch (error) {
      log.error('Error fetching recent images:', error);
      return [];
    }
  }

  async ensureImageAccessCookie() {
    const url = `${this.baseUrl}/api/images/access-cookie`;
    return this._fetchJsonWithDedupe(
      url,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
      { maxAttempts: 2, baseDelayMs: 500, jitterMs: 200 }
    );
  }

  async fetchImageSignedUrl(storagePath, campaignId = null) {
    if (!storagePath) {
      return null;
    }
    const params = new URLSearchParams({
      storage_path: storagePath,
    });
    if (campaignId) {
      params.set('campaign_id', campaignId);
    }
    const url = `${this.baseUrl}/api/images/signed-url?${params.toString()}`;
    return this._fetchJsonWithDedupe(
      url,
      { method: 'GET' },
      { maxAttempts: 2, baseDelayMs: 500, jitterMs: 200 }
    );
  }

  /**
   * Use the compatibility endpoint that accepts the same format as protobuf
   * This makes migration easier by keeping the same request format
   * @param {string} message - The message
   * @param {string} sessionId - Session ID
   * @param {string} inputType - Type of input (CHAT, NEW_CAMPAIGN, etc.)
   * @returns {Promise<Object>} Transformed response
   */
  async sendCompatRequest(message, sessionId, inputType) {
    log.debug(`Sending compat request, type: ${inputType}`);
    
    const response = await this.makeRequest('/api/chat/compat', {
      message,
      session_id: sessionId,
      input_type: inputType
    });
    
    return this.transformResponse(response);
  }

  // Campaign Management Methods
  
  /**
   * List all campaigns
   * @param {Object} options - Query options
   * @returns {Promise<Object>} Campaigns list
   */
  async listCampaigns(options = {}) {
    const params = new URLSearchParams({
      limit: String(options.limit ?? 50),
      offset: String(options.offset ?? 0),
      sort_by: options.sortBy ?? 'last_played',
      ascending: String(options.ascending ?? false)
    });

    const response = await fetch(`${this.baseUrl}/api/campaigns?${params}`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Load a specific campaign
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign data
   */
  async loadCampaign(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    const headers = {};

    // Add auth token if available (needed for character associations)
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        // Continue without auth - campaign loading still works
        log.debug('No auth token for loadCampaign:', error);
      }
    }

    const response = await fetch(`${this.baseUrl}/api/campaigns/${encodedId}`, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Save a campaign
   * @param {string} campaignId - Campaign ID
   * @param {Object} data - Campaign data
   * @returns {Promise<Object>} Save result
   */
  async saveCampaign(campaignId, data) {
    const encodedId = this.encodePathSegment(campaignId);
    return this.makeRequest(`/api/campaigns/${encodedId}/save`, data);
  }

  /**
   * Create a new campaign
   * @param {Object} campaignData - Campaign creation data
   * @returns {Promise<Object>} Created campaign
   */
  async createCampaign(campaignData) {
    return this.makeRequest('/api/campaigns', campaignData);
  }

  /**
   * Create arena quick start campaign
   * @param {Object} options - Arena options (player_count, npc_count, difficulty)
   * @returns {Promise<Object>} Arena campaign response
   */
  async createArenaQuickStart(options = {}) {
    const payload = {
      player_count: options.player_count || 1,
      npc_count: options.npc_count || 2,
      difficulty: options.difficulty || 'medium',
    };
    return this.makeRequest('/api/arena/quick-start', payload);
  }

  /**
   * Create tutorial quick start campaign
   * @returns {Promise<Object>} Tutorial campaign response with campaign_id
   */
  async createTutorialQuickStart() {
    return this.makeRequest('/api/tutorial/quick-start', {});
  }

  /**
   * Complete tutorial setup after character assignment.
   * Backward-compatible with tutorial flows that defer initialization.
   */
  async completeTutorialSetup(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this.makeRequest(`/api/tutorial/${encodedId}/complete`, {});
  }

  /**
   * Request an admin-controlled backend restart (dev recovery path).
   * Requires admin auth and ENABLE_ADMIN_BACKEND_RESTART=true on backend.
   * @param {Object} options
   * @returns {Promise<Object>} Restart scheduling response
   */
  async requestBackendRestart(options = {}) {
    const payload = {
      confirm: options.confirm || 'RESTART_BACKEND',
      reason: options.reason || 'manual_recovery',
      delay_seconds: options.delaySeconds || 2,
    };
    return this.makeRequest('/api/admin/system/restart', payload, 'POST');
  }


  /**
   * List simple campaigns
   * @returns {Promise<Object>} Simple campaigns list
   */
  async listSimpleCampaigns({ role = null, limit = 10, offset = 0 } = {}) {
    const headers = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available
    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        authLog.warn('Failed to get auth token for listSimpleCampaigns:', error);
      }
    }

    const queryParams = new URLSearchParams();
    if (role) {
      queryParams.set('role', role);
    }
    queryParams.set('limit', String(limit));
    queryParams.set('offset', String(offset));
    const queryString = queryParams.toString() ? `?${queryParams.toString()}` : '';
    const response = await fetch(`${this.baseUrl}/api/simple-campaigns${queryString}`, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Load a simple campaign (activates it for DM use)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Simple campaign data
   */
  async loadSimpleCampaign(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    // BOU-560 8H: structured_data.audio mapping dropped — DM load no longer
    // reads structured_data from REST; audio comes via socket events.
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/simple-campaigns/${encodedId}`,
      { method: 'GET' }
    );
  }

  /**
   * Get the current scene for a campaign (for scene structure UI).
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Current scene payload
   */
  async getCurrentScene(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/simple-campaigns/${encodedId}/current-scene`,
      { method: 'GET' }
    );
  }

  /**
   * Read a simple campaign without activating it (for player view)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Simple campaign data
   */
  async readSimpleCampaign(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/simple-campaigns/${encodedId}/read`,
      { method: 'GET' }
    );
  }

  /**
   * Fetch campaign messages from the fast lightweight endpoint.
   * Returns messages + minimal metadata for early narrative render.
   * @param {string} campaignId - Campaign ID
   * @param {Object} [options]
   * @param {number} [options.sinceTurn] - Lower bound (inclusive, >= turn_number).
   *   Fetch messages from this turn onwards.
   * @param {number} [options.beforeTurn] - Upper bound (exclusive, < turn_number).
   *   Fetch messages before this turn (used for "load earlier" pagination).
   * @returns {Promise<Object>} Messages payload
   */
  async fetchCampaignMessages(campaignId, { sinceTurn, beforeTurn } = {}) {
    const encodedId = this.encodePathSegment(campaignId);
    const params = new URLSearchParams();
    if (sinceTurn != null) params.set('since_turn', sinceTurn);
    if (beforeTurn != null) params.set('before_turn', beforeTurn);
    const qs = params.toString();
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/simple-campaigns/${encodedId}/messages${qs ? `?${qs}` : ''}`,
      { method: 'GET' },
      `messages-${campaignId}-s${sinceTurn ?? ''}-b${beforeTurn ?? ''}`,
    );
  }

  /**
   * Fetch all data needed for player page initial render in a single request.
   * Replaces individual calls to readSimpleCampaign, fetchRecentImages,
   * getCurrentSceneState, getCampaignRecap, getPlayerJournal, etc.
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Bootstrap payload with campaign, scene_state,
   *   characters, dm_preferences, images, recap, journal sections
   */
  async fetchPlayerBootstrap(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${encodedId}/player-bootstrap`,
      { method: 'GET' },
      `player-bootstrap-${campaignId}`,
    );
  }

  /**
   * Fetch deferred player page bootstrap sections after first render.
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Deferred bootstrap extras payload
   */
  async fetchPlayerBootstrapExtras(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${encodedId}/player-bootstrap/extras`,
      { method: 'GET' },
      `player-bootstrap-extras-${campaignId}`,
    );
  }

  /**
   * Get the currently active campaign ID from the DM interface
   * @returns {Promise<string|null>} Active campaign ID or null if none active
   */
  async getActiveCampaign() {
    const headers = { 'Content-Type': 'application/json' };
    
    // Add Auth0 token if available
    if (this.getAccessToken) {
      try {
        const token = await this.getAccessToken();
        if (token) {
          headers['Authorization'] = `Bearer ${token}`;
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for active campaign:', error);
      }
    }
    
    try {
      const response = await fetch(`${this.baseUrl}/api/active-campaign`, {
        method: 'GET',
        headers,
        // Add timeout to prevent hanging
        signal: AbortSignal.timeout(5000)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      return data.active_campaign_id;
    } catch (error) {
      // If it's a network error (backend not running), return null gracefully
      if (error.name === 'AbortError' || error.message.includes('Failed to fetch') || error.message.includes('ERR_')) {
        log.warn('Backend not accessible for active campaign check:', error.message);
        return null;
      }
      // Re-throw other errors
      throw error;
    }
  }

  // Character Management Methods

  /**
   * Get all pregenerated characters
   * @returns {Promise<Object>} List of pregenerated characters
   */
  async getPregeneratedCharacters() {
    const response = await fetch(`${this.baseUrl}/api/characters/pregenerated`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Get all pregenerated campaigns
   * @returns {Promise<Object>} List of pregenerated campaigns
   */
  async getPregeneratedCampaigns() {
    const response = await fetch(`${this.baseUrl}/api/campaigns/pregenerated`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Get visual options for character creation (gender, age, expression, pose)
   * @returns {Promise<Object>} Object with arrays for each field type
   */
  async getCharacterVisualOptions() {
    const response = await fetch(`${this.baseUrl}/api/characters/visual-options`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  async getCharacter(characterId) {
    if (!characterId) return null;
    const url = `${this.baseUrl}/api/characters/${this.encodePathSegment(characterId)}/profile`;
    return this._fetchJsonWithDedupe(url, { method: 'GET' });
  }

  /**
   * Get all characters owned by or shared with the current user
   * @param {Object} options - Query options
   * @param {string} options.characterType - Filter by type (player/npc)
   * @returns {Promise<Array>} List of user's characters
   */
  async getMyCharacters({ characterType = 'player' } = {}) {
    const params = new URLSearchParams();
    if (characterType) params.append('character_type', characterType);
    const url = `${this.baseUrl}/api/characters/mine?${params.toString()}`;
    return this._fetchJsonWithDedupe(url, { method: 'GET' });
  }

  /**
   * Generate a pregenerated character payload
   * @param {Object} characterData - Character generation data
   * @returns {Promise<Object>} Generated pregenerated character
   */
  async generatePregeneratedCharacter(characterData) {
    return this.makeRequest('/api/characters/pregenerated/generate', characterData);
  }

  /**
   * Generate a character (legacy alias)
   * @deprecated Use generatePregeneratedCharacter instead.
   */
  async generateCharacter(characterData) {
    return this.generatePregeneratedCharacter(characterData);
  }

  /**
   * Fast-generate a complete character from a free-text concept.
   * @param {string} concept - The character concept/description.
   * @param {string|null} name - Optional desired name.
   * @param {string|null} visualStyle - Optional visual style key.
   * @returns {Promise<Object>} Generated character payload.
   */
  async fastGenerateCharacter(concept, name = null, visualStyle = null) {
    const payload = { concept };
    if (name) payload.name = name;
    if (visualStyle) payload.visual_style = visualStyle;
    return this.makeRequest('/api/characters/fast-generate', payload);
  }

  /**
   * Decoupled fast text-only generation: returns name, structured identity
   * fields (character_class, race, gender, age_category, facial_expression),
   * a standalone primary_weapon highlight, description, and backstory. The
   * portrait is generated separately via generateStandaloneCharacterPortrait
   * so the two stages never block each other.
   * @param {string} concept
   * @param {string|null} name - optional name to preserve
   * @returns {Promise<{name:string, character_class:string, race:string, gender:string, age_category:string, facial_expression:string, primary_weapon:string, description:string, backstory:string, elapsed_ms:number}>}
   */
  async fastTextCharacter(concept, name = null) {
    const payload = { concept };
    if (name) payload.name = name;
    return this.makeRequest('/api/characters/fast-text', payload);
  }

  /**
   * Create a character profile for the current user
   * @param {Object} characterData - Character profile data
   * @returns {Promise<Object>} Created character profile
   */
  async createCharacterProfile(characterData) {
    return this.makeRequest('/api/characters', characterData);
  }

  /**
   * Update a character profile owned by the current user
   * @param {string} characterId - Character UUID
   * @param {Object} updates - Updated character fields
   * @returns {Promise<Object>} Updated character profile
   */
  async updateCharacterProfile(characterId, updates) {
    return this.makeRequest(
      `/api/characters/${this.encodePathSegment(characterId)}/profile`,
      updates,
      'PATCH'
    );
  }

  /**
   * Upload a portrait image file for an existing character profile.
   * @param {string} characterId - Character UUID or external ID
   * @param {File} file - Image file (png/jpeg/webp)
   * @returns {Promise<Object>} Updated character profile
   */
  async uploadCharacterPortrait(characterId, file) {
    if (!characterId) {
      throw new Error('Character ID is required for portrait upload');
    }
    if (!file) {
      throw new Error('Portrait file is required');
    }

    const headers = {};
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers.Authorization = `Bearer ${accessToken}`;
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for portrait upload:', error);
      }
    }

    const formData = new FormData();
    formData.append('portrait', file);

    const response = await fetch(
      `${this.baseUrl}/api/characters/${this.encodePathSegment(characterId)}/portrait/upload`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );

    if (!response.ok) {
      let errorMessage = `Failed to upload portrait (${response.status})`;
      try {
        const payload = await response.json();
        if (payload?.detail) {
          errorMessage = typeof payload.detail === 'string' ? payload.detail : JSON.stringify(payload.detail);
        }
      } catch (_) {
        try {
          const text = await response.text();
          if (text) errorMessage = text;
        } catch (_) {
          // ignore
        }
      }
      throw new Error(errorMessage);
    }

    return response.json();
  }

  /**
   * Delete a character profile owned by the current user
   * @param {string} characterId - Character UUID
   * @returns {Promise<Object>} Deletion result
   */
  async deleteCharacterProfile(characterId) {
    return this.makeRequest(
      `/api/characters/${this.encodePathSegment(characterId)}`,
      null,
      'DELETE'
    );
  }

  /**
   * Generate a portrait for a standalone character (no campaign required)
   * @param {Object} characterData - Character profile data
   * @param {Object} options - Generation options
   * @param {string|null} options.customPromptAdditions - Optional prompt additions
   * @param {boolean} options.enhanceWithLlm - If true, LLM enhances the prompt before generation
   * @param {string|null} options.visualStyle - Visual style theme for portrait
   * @returns {Promise<Object>} Portrait generation result
   */
  async generateStandaloneCharacterPortrait(characterData, options = {}) {
    const payload = {
      character_data: characterData,
      custom_prompt_additions: options.customPromptAdditions || null,
      enhance_with_llm: options.enhanceWithLlm || false,
      visual_style: options.visualStyle || null,
      campaign_session_id: options.campaignSessionId || null,
      portrait_request_id: options.portraitRequestId || null,
    };
    // Direct in-band delivery is faster than the webhook round-trip for
    // sub-second distilled models (flux2_dev/klein) on the synchronous path.
    if (options.useRunwareWebhook === false) {
      payload.use_runware_webhook = false;
    }
    return this.makeRequest('/api/characters/portrait/generate', payload);
  }

  /**
   * Get available visual styles for portrait generation
   * @returns {Promise<Object>} Object with styles list and default style key
   */
  async getVisualStyles() {
    return this._fetchJsonWithDedupe(`${this.baseUrl}/api/characters/visual-styles`);
  }

  /**
   * Create a one-time session invite token.
   * @param {string} sessionId - Session ID to share
   * @param {Object} options - Additional options
   * @param {boolean} options.regenerate - Invalidate existing invites first
   * @param {number|null} options.expiresInMinutes - Optional expiration window
   * @param {'player'|'dm'} options.seatType - Seat role the joiner should fill (default 'player')
   * @returns {Promise<Object>} Invite response
   */
  async createSessionInvite(sessionId, { regenerate = false, expiresInMinutes = null, seatType = 'player' } = {}) {
    const payload = {
      session_id: sessionId,
      regenerate,
      seat_type: seatType,
    };
    if (expiresInMinutes) {
      payload.expires_in_minutes = expiresInMinutes;
    }
    return this.makeRequest('/api/sessions/share', payload);
  }

  /**
   * Join a shared session using an invite token.
   * @param {string} inviteToken - Invite token provided by the session owner
   * @returns {Promise<Object>} Join response
   */
  async joinSessionByInvite(inviteToken) {
    return this.makeRequest('/api/sessions/join', {
      invite_token: inviteToken,
    });
  }

  /**
   * Get character voices
   * @returns {Promise<Object>} Available voices
   */
  async getCharacterVoices() {
    const response = await fetch(`${this.baseUrl}/api/characters/voices`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Preview a voice with sample text
   * @param {string} voiceId - Voice ID to preview
   * @param {string} text - Text to speak
   * @returns {Promise<Object>} Audio URL
   */
  async previewVoice(voiceId, text) {
    return this.makeRequest('/api/tts/preview', { voice_id: voiceId, text });
  }

  /**
   * Get Cornelius-voiced TTS narration for a prompt.
   * @param {string} text - Text to narrate
   * @returns {Promise<Object>} { audio_url, ... }
   */
  async getCorneliusNarration(text) {
    return this.makeRequest('/api/tts/preview', { voice_id: 'cornelius', text });
  }

  /**
   * Bind an already-created character (via createCharacterProfile) to a new
   * Red Road campaign and mark the tutorial complete. No LLM call — the
   * character, its portrait and its stage cutout all came from Fast Create.
   * @param {{ existing_character_id: string, skip_intro?: boolean }} payload
   * @returns {Promise<{ character_id: string, campaign_id: string }>}
   */
  async submitPlayNowOnboarding(payload) {
    return this.makeRequest('/api/onboarding/play-now', payload);
  }

  /**
   * Guided Forge interview's one LLM call: combines the three raw answers
   * (plus any explicit identity picks, omitted when left to Fate) into a
   * single polished concept, handed to the same generation flow Fast Create
   * uses (fastTextCharacter).
   * @param {{q1_memory: string, q2_combat: string, q3_purpose: string, name?: string|null, gender?: string|null, race?: string|null}} payload
   * @returns {Promise<{concept: string}>}
   */
  async sanitizeOnboardingConcept(payload) {
    return this.makeRequest('/api/onboarding/sanitize-concept', payload);
  }

  // Image Generation Methods

  /**
   * Generate an image
   * @param {Object} imageData - Image generation data
   * @returns {Promise<Object>} Generated image
   */
  async generateImage(imageData) {
    return this.makeRequest('/api/generate-image', imageData);
  }

  /**
   * Get available image models
   * @returns {Promise<Object>} Available models
   */
  async getImageModels() {
    const response = await fetch(`${this.baseUrl}/api/image-models`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Get ordered turn pipeline steps for progress UI.
   * @returns {Promise<Object>} Turn pipeline steps payload
   */
  async getTurnPipelineSteps() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/turn-pipeline/steps`,
      { method: 'GET' },
      { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 }
    );
  }

  // TTS Methods

  /**
   * Get TTS queue status
   * @returns {Promise<Object>} Queue status
   */
  async getTTSQueueStatus(sessionId = null) {
    const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
    const url = `${this.baseUrl}/api/tts/queue/status${query}`;
    return this._fetchJsonWithDedupe(url, { method: 'GET' }, { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 });
  }

  /**
   * Stop TTS playback
   * @returns {Promise<Object>} Stop result
   */
  async stopTTS(sessionId = null) {
    const payload = sessionId ? { session_id: sessionId } : {};
    return this.makeRequest('/api/tts/queue/stop', payload);
  }

  /**
   * Get available TTS voices
   * @returns {Promise<Object>} List of available voices
   */
  async getTTSVoices() {
    // Simple in-memory cache to avoid duplicate bursts from multiple components
    if (!this._voicesCache) {
      this._voicesCache = { data: null, ts: 0 };
    }
    const now = Date.now();
    const cacheTtlMs = 60 * 1000; // 1 minute cache
    if (this._voicesCache.data && (now - this._voicesCache.ts) < cacheTtlMs) {
      return this._voicesCache.data;
    }

    const url = `${this.baseUrl}/api/tts/voices`;
    const data = await this._fetchJsonWithDedupe(url, { method: 'GET' }, { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 });
    this._voicesCache = { data, ts: Date.now() };
    return data;
  }

  /**
   * Stop TTS queue playback
   * @returns {Promise<Object>} Stop result
   */
  async stopTTSQueue(sessionId = null) {
    const payload = sessionId ? { session_id: sessionId } : {};
    return this.makeRequest('/api/tts/queue/stop', payload);
  }

  /**
   * Get TTS queue status
   * @param {string} sessionId - Optional session ID
   * @returns {Promise<Object>} Queue status
   */
  async getQueueStatus(sessionId = null) {
    const url = sessionId
      ? `${this.baseUrl}/api/tts/queue/status?session_id=${encodeURIComponent(sessionId)}`
      : `${this.baseUrl}/api/tts/queue/status`;
    const response = await fetch(url, { method: 'GET' });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Clear the audio queue
   * @param {string} sessionId - Optional session ID
   * @returns {Promise<Object>} Clear result
   */
  async clearAudioQueue(sessionId = null) {
    const payload = sessionId ? { session_id: sessionId } : {};
    return this.makeRequest('/api/tts/queue/clear', payload);
  }

  /**
   * Skip all pending audio - marks database records as FAILED and clears queue.
   * Use this instead of clearAudioQueue when you want to ensure audio
   * doesn't replay when the queue is fetched again.
   * @param {string} sessionId - Session/campaign ID
   * @returns {Promise<Object>} Skip result with cancelled_count
   */
  async skipAudioQueue(sessionId) {
    const payload = sessionId ? { session_id: sessionId } : {};
    return this.makeRequest('/api/tts/queue/skip', payload);
  }

  /**
   * Skip audio queue for the current user only (player view).
   * Marks all pending audio for this user as played in the database.
   * Use this in player view - it only affects the current user, not other players.
   * @param {string} sessionId - Session/campaign ID
   * @returns {Promise<Object>} Skip result with skipped_count
   */
  async skipUserAudioQueue(sessionId) {
    const payload = sessionId ? { session_id: sessionId } : {};
    return this.makeRequest('/api/tts/queue/skip-user', payload);
  }

  /**
   * Align the current user's audio cursor with the campaign's current position.
   * @param {string} sessionId - Session/campaign ID
   * @returns {Promise<Object>} Alignment result
   */
  async alignUserAudioQueue(sessionId) {
    const payload = sessionId ? { session_id: sessionId } : {};
    // Viewer entry blocks the seat modal while this is in flight — a bounded
    // timeout keeps a black-holed request from trapping the user there.
    return this.makeRequest(
      '/api/tts/queue/align-user',
      payload,
      'POST',
      AbortSignal.timeout(15000),
    );
  }

  /**
   * Get available voices (alias for getTTSVoices for consistency)
   * @returns {Promise<Object>} List of available voices
   */
  async getAvailableVoices() {
    return this.getTTSVoices();
  }

  /**
   * Trigger debug streaming chunks over WebSocket
   * @param {string} sessionId
   * @param {Object} options
   */
  async triggerDebugStream(sessionId, options = {}) {
    if (!sessionId) {
      throw new Error('sessionId is required to trigger debug stream');
    }
    const payload = {
      session_id: sessionId,
    };
    if (options.narrative) {
      payload.narrative = options.narrative;
    }
    if (options.playerResponse) {
      payload.player_response = options.playerResponse;
    }
    return this.makeRequest('/api/debug/streaming-test', payload);
  }

  /**
   * Run Storyteller with a fixed debug prompt
   * @param {string} sessionId
   * @param {Object} options
   */
  async runDebugDm(sessionId, options = {}) {
    if (!sessionId) {
      throw new Error('sessionId is required to run debug DM');
    }
    const payload = {
      session_id: sessionId,
    };
    if (options.prompt) {
      payload.prompt = options.prompt;
    }
    return this.makeRequest('/api/debug/run-streaming-direct', {
      ...payload,
      include_scene_context: options.includeSceneContext ?? true,
      include_conversation_context: options.includeConversationContext ?? true,
      analysis: options.analysis ?? null,
      force_audio: options.forceAudio ?? true,
    });
  }

  /**
   * Get TTS availability status
   * @returns {Promise<Object>} TTS availability info
   */
  async getTTSAvailability() {
    const headers = { 'Content-Type': 'application/json' };
    
    // Add Auth0 token if available
    if (this.auth0Client?.isAuthenticated) {
      try {
        const token = await this.auth0Client.getAccessTokenSilently();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for TTS availability:', error);
      }
    }
    
    const response = await fetch(`${this.baseUrl}/api/tts/availability`, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Get connected players for a campaign (429-aware with dedupe)
   * @param {string} campaignId
   * @returns {Promise<Object>} { success, connected_players, count }
   */
  async getConnectedPlayers(campaignId) {
    if (!campaignId) throw new Error('campaignId is required');
    const encoded = this.encodePathSegment(campaignId);
    const url = `${this.baseUrl}/api/campaigns/${encoded}/connected-players`;
    // Use limited retries and jitter to avoid hammering when rate-limited
    return this._fetchJsonWithDedupe(url, { method: 'GET' }, { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 });
  }

  /**
   * Get TTS providers
   * @returns {Promise<Object>} List of TTS providers
   */
  async getTTSProviders() {
    const headers = { 'Content-Type': 'application/json' };

    // Add Auth0 token if available
    if (this.auth0Client?.isAuthenticated) {
      try {
        const token = await this.auth0Client.getAccessTokenSilently();
        headers['Authorization'] = `Bearer ${token}`;
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for TTS providers:', error);
      }
    }

    const response = await fetch(`${this.baseUrl}/api/tts/providers`, { headers });
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Switch image generation model
   * @param {Object} modelData - Model switching data
   * @returns {Promise<Object>} Switch result
   */
  async switchImageModel(modelData) {
    return this.makeRequest('/api/image-models/switch', modelData);
  }

  /**
   * Generate a new campaign
   * @param {Object} campaignData - Campaign generation data
   * @returns {Promise<Object>} Generated campaign
   */
  async generateCampaign(campaignData) {
    return this.makeRequest('/api/campaigns/generate', campaignData);
  }

  /**
   * Assign character to campaign slot
   * @param {string} campaignId - Campaign ID
   * @param {Object} slotData - Slot assignment data
   * @returns {Promise<Object>} Assignment result
   */
  async assignCharacterToSlot(campaignId, slotData) {
    const encodedId = this.encodePathSegment(campaignId);
    return this.makeRequest(`/api/campaigns/${encodedId}/characters/slot`, slotData);
  }

  /**
   * Get all characters in a campaign with enriched data (portraits, etc.)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Response with characters array
   */
  async getCampaignCharacters(campaignId, options = {}) {
    const encodedId = this.encodePathSegment(campaignId);
    const params = new URLSearchParams();
    if (options.activeOnly !== undefined && options.activeOnly !== null) {
      params.set('active_only', String(options.activeOnly));
    }
    if (options.seatOnly !== undefined && options.seatOnly !== null) {
      params.set('seat_only', String(options.seatOnly));
    }
    const queryString = params.toString() ? `?${params.toString()}` : '';
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${encodedId}/characters${queryString}`
    );
  }

  /**
   * Initialize a campaign
   * @param {Object} initData - Campaign initialization data
   * @returns {Promise<Object>} Initialization result
   */
  async initializeCampaign(initData) {
    return this.makeRequest('/api/campaigns/initialize', initData);
  }

  /**
   * Delete a campaign
   * @param {string} campaignId - Campaign ID to delete
   * @returns {Promise<Object>} Deletion result
   */
  async deleteCampaign(campaignId) {
    const encodedId = this.encodePathSegment(campaignId);
    return this.makeRequest(`/api/campaigns/${encodedId}`, null, 'DELETE');
  }

  /**
   * Get voice activity status
   * @param {string} sessionId - Session ID
   * @returns {Promise<Object>} Voice activity status
   */
  async getVoiceActivity(sessionId) {
    const encodedSessionId = this.encodePathSegment(sessionId);
    const url = `${API_CONFIG.VOICE_ACTIVITY_ENDPOINT}/${encodedSessionId}`;
    return this._fetchJsonWithDedupe(url, { method: 'GET' }, { maxAttempts: 3, baseDelayMs: 800, jitterMs: 250 });
  }

  /**
   * Get auth providers
   * @returns {Promise<Object>} List of auth providers
   */
  async getAuthProviders() {
    const response = await fetch(`${this.baseUrl}/api/auth/providers`);
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
    return response.json();
  }

  /**
   * Verify Auth0 token
   * @param {string} token - Auth0 token to verify
   * @returns {Promise<Object>} Verification result
   */
  async verifyAuth0Token(token) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort('backend-verification-timeout'), 15000);
    try {
      const response = await fetch(`${this.baseUrl}/api/auth0/verify`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal,
      });
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        let body = null;
        try {
          body = await response.json();
          // Extract the error detail from FastAPI response
          errorMessage = body?.detail || body?.message || errorMessage;
        } catch (_) {
          // If JSON parse fails, try text
          try {
            const text = await response.text();
            if (text) errorMessage = text;
          } catch (_) {
            // ignore
          }
        }
        const err = new Error(errorMessage);
        err.status = response.status;
        err.body = body;
        err.headers = response.headers;  // Pass headers for X-Email-Verification-Required check
        throw err;
      }
      return response.json();
    } catch (error) {
      if (error?.name === 'AbortError' || controller.signal.aborted) {
        const err = new Error('Backend verification timed out');
        err.name = 'BackendVerificationTimeoutError';
        err.code = 'BACKEND_VERIFY_TIMEOUT';
        throw err;
      }
      throw error;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  // Portrait Generation Methods

  /**
   * Generate a portrait for a character
   * @param {string} characterId - Character ID
   * @param {string} campaignId - Campaign ID
   * @param {Object} options - Generation options
   * @returns {Promise<Object>} Portrait generation result
   */
  async generateCharacterPortrait(characterId, campaignId, options = {}) {
    const headers = { 'Content-Type': 'application/json' };

    // Add Auth0 token if available
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for portrait generation:', error);
      }
    }

    const payload = {
      character_id: characterId,
      campaign_id: campaignId,
      regenerate: options.regenerate || false,
      custom_prompt_additions: options.custom_prompt_additions || null,
      character_data: options.character_data || null,
      enhance_with_llm: options.enhance_with_llm || false,
      visual_style: options.visual_style || null,
    };

    const response = await fetch(`${this.baseUrl}/api/characters/${this.encodePathSegment(characterId)}/portrait/generate`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload)
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to generate portrait: ${errorText}`);
    }

    return response.json();
  }

  /**
   * Generate persistent Stage Cutout assets for a player character from its Portrait.
   * @param {string} characterId - Character UUID or external ID
   * @param {Object} options - Generation options
   * @returns {Promise<Object>} Updated character profile with image_assets
   */
  async generateCharacterStageAssets(characterId, options = {}) {
    if (!characterId) {
      throw new Error('Character ID is required for Stage Cutout generation');
    }
    return this.makeRequest(
      `/api/characters/${this.encodePathSegment(characterId)}/stage-assets/generate`,
      {
        regenerate: options.regenerate || false,
        custom_prompt_additions: options.custom_prompt_additions || null,
        visual_style: options.visual_style || null,
        image_model: options.image_model || null,
        session_id: options.session_id || null,
      },
      'POST'
    );
  }

  /**
   * Retry only transparent Stage Cutout isolation from an existing full-body source.
   * @param {string} characterId - Character UUID or external ID
   * @param {Object} options - Retry options
   * @returns {Promise<Object>} Updated character profile with image_assets
   */
  async retryCharacterStageCutout(characterId, options = {}) {
    if (!characterId) {
      throw new Error('Character ID is required for Stage Cutout retry');
    }
    return this.makeRequest(
      `/api/characters/${this.encodePathSegment(characterId)}/stage-assets/retry-cutout`,
      {
        session_id: options.session_id || null,
      },
      'POST'
    );
  }

  /**
   * Get portrait information for a character
   * @param {string} characterId - Character ID
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Portrait information
   */
  async getCharacterPortrait(characterId, campaignId) {
    const headers = {};

    // Add Auth0 token if available
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for portrait retrieval:', error);
      }
    }

    const response = await fetch(
      `${this.baseUrl}/api/characters/${this.encodePathSegment(characterId)}/portrait?campaign_id=${this.encodePathSegment(campaignId)}`,
      { headers }
    );

    if (!response.ok) {
      throw new Error(`Failed to get portrait: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Update character visual metadata
   * @param {string} characterId - Character ID
   * @param {string} campaignId - Campaign ID
   * @param {Object} visualData - Visual field updates
   * @returns {Promise<Object>} Update result
   */
  async updateCharacterVisuals(characterId, campaignId, visualData) {
    const headers = { 'Content-Type': 'application/json' };

    // Add Auth0 token if available
    if (this.getAccessToken) {
      try {
        const accessToken = await this.getAccessToken();
        if (accessToken) {
          headers['Authorization'] = `Bearer ${accessToken}`;
        }
      } catch (error) {
        authLog.warn('Failed to get Auth0 token for visual update:', error);
      }
    }

    const response = await fetch(
      `${this.baseUrl}/api/characters/${this.encodePathSegment(characterId)}?campaign_id=${this.encodePathSegment(campaignId)}`,
      {
        method: 'PATCH',
        headers,
        body: JSON.stringify(visualData)
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to update character visuals: ${errorText}`);
    }

    return response.json();
  }

  // ============================================================================
  // ROOM & SEAT MANAGEMENT API METHODS
  // ============================================================================

  /**
   * Get full room state with all seats for a campaign
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} Room state with seats array
   */
  async getRoomState(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}`
    );
  }

  /**
   * Get lightweight room summary
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} Room summary
   */
  async getRoomSummary(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/summary`
    );
  }

  /**
   * List room summaries for accessible campaigns
   * @param {Object} options
   * @param {string} [options.role='player'] - Role filter
   * @returns {Promise<Object>} Room summaries response
   */
  async listRoomSummaries({ role = 'player' } = {}) {
    const query = new URLSearchParams();
    if (role) {
      query.set('role', role);
    }
    const qs = query.toString() ? `?${query.toString()}` : '';
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/summaries${qs}`
    );
  }

  /**
   * List all seats for a campaign
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Array>} Array of seats
   */
  async listSeats(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats`
    );
  }

  /**
   * Get specific seat details
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID
   * @returns {Promise<Object>} Seat details
   */
  async getSeat(campaignId, seatId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}`
    );
  }

  /**
   * Occupy a seat (claim it for current user)
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID to occupy
   * @returns {Promise<Object>} Updated seat
   */
  async occupySeat(campaignId, seatId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}/occupy`,
      { method: 'POST' }
    );
  }

  /**
   * Release a seat (free it from current user)
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID to release
   * @returns {Promise<Object>} Updated seat
   */
  async releaseSeat(campaignId, seatId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}/release`,
      { method: 'POST' }
    );
  }

  /**
   * Vacate a seat (DM only - force remove player)
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID to vacate
   * @param {Object} options - Vacate options
   * @param {boolean} options.notify_user - Whether to notify the vacated user
   * @returns {Promise<Object>} Updated seat
   */
  async vacateSeat(campaignId, seatId, options = { notify_user: true }) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}/vacate`,
      {
        method: 'POST',
        body: JSON.stringify(options)
      }
    );
  }

  /**
   * Remove character from a seat (DM or seat owner). Preserves seat owner;
   * clears character_id and character_instance_id.
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID
   * @returns {Promise<Object>} { seat }
   */
  async removeCharacterFromSeat(campaignId, seatId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}/remove-character`,
      {
        method: 'POST',
        body: JSON.stringify({})
      }
    );
  }

  /**
   * Assign character to a seat
   * @param {string} campaignId - Campaign/session ID
   * @param {string} seatId - Seat ID
   * @param {Object} characterData - Character creation data
   * @returns {Promise<Object>} Result with character_id
   */
  async assignCharacterToSeat(campaignId, seatId, characterData) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/seats/${this.encodePathSegment(seatId)}/assign-character`,
      {
        method: 'POST',
        body: JSON.stringify({ character_data: characterData })
      }
    );
  }

  /**
   * Start the campaign (DM only)
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} Campaign start result
   */
  async startCampaign(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/start`,
      { method: 'POST' }
    );
  }

  /**
   * Resume a failed campaign setup
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} Resume result
   */
  async resumeCampaignSetup(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/resume-setup`,
      { method: 'POST' }
    );
  }

  /**
   * Get campaign state including setup status
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} Campaign state
   */
  async getCampaignState(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}${this.v2Prefix}/rooms/${this.encodePathSegment(campaignId)}/state`
    );
  }

  /**
   * Get turn processing status with stale detection.
   * Lightweight endpoint for polling when a turn is processing to detect failures.
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} { is_processing, resume_turn, was_stale, stale_reason }
   */
  async getTurnStatus(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${this.encodePathSegment(campaignId)}/turn-status`,
      { method: 'GET' },
      { maxAttempts: 1, baseDelayMs: 0 }  // No retries for status polling
    );
  }

  /**
   * Get lightweight campaign version for reconnect diffing.
   * Used to decide whether a full state refetch is needed after socket reconnect.
   *
   * @param {string} campaignId
   * @returns {Promise<{campaign_id: string, version: string, turn_number: number}>}
   */
  async getCampaignVersion(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${this.encodePathSegment(campaignId)}/version`,
      { method: 'GET' },
      { maxAttempts: 1, baseDelayMs: 0 },
    );
  }

  // ==========================================================================
  // Game Preferences API
  // ==========================================================================

  /**
   * Get DM preferences for the current user
   * @param {Object} [options] - Optional parameters
   * @param {boolean} [options.tutorial] - If true, apply tutorial-specific defaults
   * @returns {Promise<Object>} DM preferences
   */
  async getDMPreferences({ tutorial } = {}) {
    const params = tutorial ? '?tutorial=true' : '';
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/dm${params}`
    );
  }

  /**
   * Update DM preferences for the current user
   * @param {Object} preferences - Preference fields to update
   * @returns {Promise<Object>} Updated DM preferences
   */
  async updateDMPreferences(preferences) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/dm`,
      {
        method: 'PUT',
        body: JSON.stringify(preferences)
      }
    );
  }

  /**
   * Get available model options for DM preferences
   * @returns {Promise<Object>} Model options with value, label, and description
   */
  async getModelOptions() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/model-options`
    );
  }

  /**
   * Get available image generation model options
   * @returns {Promise<Object>} Image model options with value, label, description
   */
  async getImageModelOptions() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/image-model-options`
    );
  }

  /**
   * Get player preferences for the current user
   * @returns {Promise<Object>} Player preferences
   */
  async getPlayerPreferences() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/player`
    );
  }

  /**
   * Update player preferences for the current user
   * @param {Object} preferences - Preference fields to update
   * @returns {Promise<Object>} Updated player preferences
   */
  async updatePlayerPreferences(preferences) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/player`,
      {
        method: 'PUT',
        body: JSON.stringify(preferences)
      }
    );
  }

  /**
   * Card decks visible to the current user.
   * Admin sees all on-disk decks; non-admin sees the server-side allow-list.
   * @returns {Promise<{decks: Array<{name: string, card_count: number, actions: string[], cards: Record<string,string>}>, selected: string, default: string}>}
   */
  async getAvailableCardDecks() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/player/available-decks`
    );
  }

  /**
   * Get gameplay preferences for the current user
   * @returns {Promise<Object>} Gameplay preferences
   */
  async getGameplayPreferences() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/gameplay`
    );
  }

  /**
   * Update gameplay preferences for the current user
   * @param {Object} preferences - Preference fields to update
   * @returns {Promise<Object>} Updated gameplay preferences
   */
  async updateGameplayPreferences(preferences) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/gameplay`,
      {
        method: 'PUT',
        body: JSON.stringify(preferences)
      }
    );
  }

  /**
   * Get campaign personality options (DM style dropdowns)
   * @returns {Promise<Object>} Campaign personality options
   */
  async getCampaignPersonalityOptions() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/campaign-personality-options`
    );
  }

  /**
   * Get campaign settings
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Campaign settings
   */
  async getCampaignSettings(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/campaign/${this.encodePathSegment(campaignId)}`
    );
  }

  /**
   * Update campaign settings
   * @param {string} campaignId - Campaign ID
   * @param {Object} settings - Settings fields to update
   * @returns {Promise<Object>} Updated campaign settings
   */
  async updateCampaignSettings(campaignId, settings) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/preferences/campaign/${this.encodePathSegment(campaignId)}`,
      {
        method: 'PUT',
        body: JSON.stringify(settings)
      }
    );
  }

  /**
   * Submit thumbs up/down feedback for a generated image
   * @param {string} metadataId - Image metadata UUID
   * @param {string|null} feedback - "up", "down", or null to clear
   * @returns {Promise<Object>} Updated feedback state
   */
  async submitImageFeedback(metadataId, feedback) {
    return this.makeRequest(
      `/api/images/${this.encodePathSegment(metadataId)}/feedback`,
      { feedback },
      'POST'
    );
  }

  /**
   * Update campaign metadata (name, description, player_count, etc.)
   * @param {string} campaignId - Campaign ID
   * @param {Object} updates - Fields to update (name, description, setting, player_count, status)
   * @returns {Promise<Object>} Updated campaign info
   */
  async updateCampaign(campaignId, updates) {
    return this.makeRequest(`/api/campaigns/${this.encodePathSegment(campaignId)}`, updates, 'PATCH');
  }

  /**
   * Get active combat data for a campaign (including combat log)
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Combat data with combat_log, or {active: false} if no combat
   */
  async getCombatData(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/combat/campaign/${this.encodePathSegment(campaignId)}`
    );
  }

  /**
   * Acknowledge/dismiss the combat-end state for a campaign.
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Object>} Ack result
   */
  async acknowledgeCombatEnd(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/combat/campaign/${this.encodePathSegment(campaignId)}/ack-end`,
      { method: 'POST' },
      { maxAttempts: 2, baseDelayMs: 500 },
    );
  }

  /**
   * Get the current scene state for a campaign.
   * @param {string} campaignId - Campaign ID
   * @param {Object} [options]
   * @param {boolean} [options.cacheBust] - BOU-2026: append a unique query
   *   param so this call gets a distinct in-flight-dedupe key
   *   (`_fetchJsonWithDedupe` dedupes by `method + url + body`). Without this,
   *   a caller that MUST see a just-written server-side change (e.g. the
   *   post-stage-hotspot-travel refresh) can silently piggyback onto an
   *   already-in-flight, pre-change GET to this same URL and resolve with the
   *   stale payload instead of issuing its own fresh request.
   * @returns {Promise<Object>} Scene state payload
   */
  async getCurrentSceneState(campaignId, { cacheBust = false } = {}) {
    const base = `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}/current`;
    const url = cacheBust ? `${base}?_=${Date.now()}` : base;
    return this._fetchJsonWithDedupe(url);
  }

  /**
   * Get a specific scene state for a campaign.
   * @param {string} campaignId - Campaign ID
   * @param {string} sceneId - Scene ID
   * @returns {Promise<Object>} Scene state payload
   */
  async getSceneState(campaignId, sceneId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}/${this.encodePathSegment(sceneId)}`
    );
  }

  /**
   * List recent scenes for a campaign.
   * @param {string} campaignId - Campaign ID
   * @param {number} limit - Maximum scenes to return
   * @returns {Promise<Object>} List of scenes
   */
  async getRecentScenes(campaignId, limit = 5) {
    const query = limit ? `?limit=${encodeURIComponent(limit)}` : '';
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}${query}`
    );
  }

  /**
   * Get scene map/diorama image if one has been generated.
   * @param {string} campaignId - Campaign ID
   * @param {string} sceneId - Scene identifier
   * @returns {Promise<Object>} Scene map data with 'available' boolean and image URLs
   */
  async getSceneMap(campaignId, sceneId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}/${this.encodePathSegment(sceneId)}/map`
    );
  }

  // =============================================================================
  // Tavern Mode - One-on-one character conversations
  // =============================================================================

  /**
   * Get characters available for tavern conversations
   * Returns user's own characters and featured tavern NPCs
   * @returns {Promise<Object>} Characters response with user_characters, featured_npcs, total
   */
  async getTavernCharacters() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/tavern/characters`,
      { credentials: 'omit' },
    );
  }

  /**
   * Start a tavern conversation with a character
   * @param {string} characterId - ID of the character to converse with
   * @param {Object} options - Optional settings
   * @param {boolean} options.freshStart - If true, start a new conversation (preserves old ones)
   * @param {string} options.sessionId - Optional specific session to resume
   * @returns {Promise<Object>} Session info and character details
   */
  async startTavernConversation(characterId, options = {}) {
    const { freshStart = false, sessionId = null, mode = 'conversation' } = options;
    return this.makeRequest('/api/tavern/start-conversation', {
      character_id: characterId,
      fresh_start: freshStart,
      session_id: sessionId,
      mode,
    }, 'POST');
  }

  /**
   * Get all conversations with a character
   * @param {string} characterId - Character ID
   * @returns {Promise<Object>} List of conversations
   */
  async getTavernConversations(characterId) {
    return this._fetchJsonWithDedupe(`${this.baseUrl}/api/tavern/characters/${this.encodePathSegment(characterId)}/conversations`);
  }

  /**
   * Get conversation history for a tavern session
   * @param {string} sessionId - Tavern session ID
   * @returns {Promise<Object>} Conversation history with messages
   */
  async getTavernHistory(sessionId) {
    return this._fetchJsonWithDedupe(`${this.baseUrl}/api/tavern/session/${this.encodePathSegment(sessionId)}/history`);
  }

  /**
   * Get cards for a tavern session (for history loading on refresh)
   * @param {string} sessionId - Tavern session ID
   * @returns {Promise<Object>} Cards for the session
   */
  async getTavernCards(sessionId) {
    return this._fetchJsonWithDedupe(`${this.baseUrl}/api/tavern/session/${this.encodePathSegment(sessionId)}/cards`);
  }

  /**
   * End a tavern conversation session
   * @param {string} sessionId - Tavern session ID
   * @returns {Promise<Object>} Success message
   */
  async endTavernSession(sessionId) {
    return this.makeRequest(`/api/tavern/session/${this.encodePathSegment(sessionId)}`, null, 'DELETE');
  }

  /**
   * Get the pre-generated tutorial transition fallback image URL.
   * Used by Quick Start flow (no tavern session).
   * @returns {Promise<Object>} { fallback_image_url: string|null }
   */
  async getTutorialTransitionFallback() {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/tavern/tutorial-transition-fallback`,
      {},
      { maxAttempts: 1 },
    );
  }

  /**
   * Fetch the current quest-loop view state for a campaign.
   * Backed by GET /api/quests/view-state?campaign_id=<id>.
   * Used by useQuestLoopState's polling loop.
   *
   * Sends If-None-Match with the previous ETag so the backend can
   * short-circuit unchanged polls to 304. On 304 we return a deep-cloned
   * copy of the cached body so callers see a stable JSON object across
   * polls and any caller mutation can't poison the cache.
   *
   * Preserves the in-flight dedupe + 429/503 backoff + auth-refresh +
   * 401 onAuthError behavior from `_fetchJsonWithDedupe`. See BOU-750.
   */
  async getQuestViewState(campaignId) {
    if (!campaignId) {
      throw new Error('getQuestViewState requires campaignId');
    }
    const params = new URLSearchParams({ campaign_id: String(campaignId) });
    const url = `${this.baseUrl}/api/quests/view-state?${params.toString()}`;
    const dedupeKey = `GET ${url}`;
    if (this.inflight.has(dedupeKey)) {
      return this.inflight.get(dedupeKey);
    }

    const cacheKey = String(campaignId);
    const cached = this._readQuestViewStateCacheEntry(cacheKey);

    const promise = this._fetchQuestViewStateWithRetry({
      url,
      cacheKey,
      cached,
    }).finally(() => this.inflight.delete(dedupeKey));

    this.inflight.set(dedupeKey, promise);
    return promise;
  }

  /**
   * Per-campaign ETag cache for /api/quests/view-state. Bounded by
   * _QUEST_VIEW_STATE_CACHE_LIMIT (LRU); cleared on 401. Keys are
   * campaign UUIDs as strings; values are { etag, body }.
   */
  _readQuestViewStateCacheEntry(cacheKey) {
    if (!this._questViewStateCache) {
      this._questViewStateCache = new Map();
    }
    return this._questViewStateCache.get(cacheKey) || null;
  }

  _writeQuestViewStateCacheEntry(cacheKey, etag, body) {
    if (!this._questViewStateCache) {
      this._questViewStateCache = new Map();
    }
    // LRU: re-insert moves to end; evict oldest when over the cap.
    this._questViewStateCache.delete(cacheKey);
    this._questViewStateCache.set(cacheKey, { etag, body });
    const LIMIT = 8;
    while (this._questViewStateCache.size > LIMIT) {
      const oldest = this._questViewStateCache.keys().next().value;
      this._questViewStateCache.delete(oldest);
    }
  }

  _clearQuestViewStateCache() {
    if (this._questViewStateCache) {
      this._questViewStateCache.clear();
    }
  }

  async _fetchQuestViewStateWithRetry({ url, cacheKey, cached }) {
    const MAX_ATTEMPTS = 3;
    const BASE_DELAY_MS = 800;
    let lastErr = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt += 1) {
      const headers = {};
      if (cached?.etag) {
        headers['If-None-Match'] = cached.etag;
      }
      if (this.getAccessToken) {
        try {
          const token = await this.getAccessToken();
          if (token) {
            headers.Authorization = `Bearer ${token}`;
          }
        } catch (_) {
          // Non-fatal; fall through unauthenticated.
        }
      }

      let response;
      try {
        response = await fetch(url, {
          method: 'GET',
          headers,
          credentials: 'include',
        });
      } catch (err) {
        lastErr = err;
        if (attempt < MAX_ATTEMPTS) {
          await this.sleep(BASE_DELAY_MS * Math.pow(2, attempt - 1));
          continue;
        }
        throw err;
      }

      if (response.status === 429
          || (response.status === 503 && response.headers.has('retry-after'))) {
        const retryHeader = response.headers.get('retry-after');
        const retryMs = this.parseRetryAfter(retryHeader)
          ?? BASE_DELAY_MS * Math.pow(2, attempt - 1);
        if (attempt < MAX_ATTEMPTS) {
          await this.sleep(retryMs);
          continue;
        }
      }

      if (response.status === 304 && cached) {
        return this._cloneQuestViewStateBody(cached.body);
      }

      if (!response.ok) {
        if (response.status === 401) {
          this._clearQuestViewStateCache();
          if (this.onAuthError) {
            this.onAuthError(new Error('Authentication token expired'));
          }
        }
        const errPayload = await this._parseErrorPayload(response);
        throw this._buildHttpError(response, errPayload);
      }

      const body = await response.json();
      const etag = response.headers.get('etag');
      if (etag) {
        this._writeQuestViewStateCacheEntry(cacheKey, etag, body);
      } else if (this._questViewStateCache) {
        this._questViewStateCache.delete(cacheKey);
      }
      return this._cloneQuestViewStateBody(body);
    }

    throw lastErr || new Error('Request failed');
  }

  _cloneQuestViewStateBody(body) {
    // Deep-clone so callers can mutate freely without poisoning the
    // cached entry that may be returned for a future 304.
    if (typeof structuredClone === 'function') {
      return structuredClone(body);
    }
    return JSON.parse(JSON.stringify(body));
  }

  /**
   * Fetch the quest board (available quests + recommended quest id) for a campaign.
   * Backed by GET /api/quests/board?campaign_id=<id>.
   */
  async getQuestBoard(campaignId) {
    if (!campaignId) {
      throw new Error('getQuestBoard requires campaignId');
    }
    const params = new URLSearchParams({ campaign_id: String(campaignId) });
    const url = `${this.baseUrl}/api/quests/board?${params.toString()}`;
    return this._fetchJsonWithDedupe(url, { method: 'GET' });
  }

  /**
   * Tell the backend the player has opened the quest board. Consumes any
   * out-of-band classifier signals stored on quest_loop_state.pending_signals.
   * Backed by POST /api/quests/board/open.
   */
  async openQuestBoard(campaignId) {
    if (!campaignId) {
      throw new Error('openQuestBoard requires campaignId');
    }
    const url = `${this.baseUrl}/api/quests/board/open`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  /**
   * Confirm the player is ready to depart from the quest giver.
   * Backed by POST /api/quests/depart.
   */
  async confirmDeparture(campaignId) {
    if (!campaignId) {
      throw new Error('confirmDeparture requires campaignId');
    }
    const url = `${this.baseUrl}/api/quests/depart`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  /**
   * Abandon a quest-giver sendoff the player never finished draining.
   * Backed by POST /api/quests/depart/abandon-audio (BOU-2270).
   *
   * `/depart` spares the already-synthesized sendoff so it can finish speaking.
   * If it never drains — blocked autoplay, dropped socket — those rows stay
   * queued and replay inside the quest. useQuestDepartureHold calls this from
   * its hard-timeout backstop, bounded to the turn it was holding for.
   */
  async abandonDepartureAudio(campaignId, throughTurn) {
    if (!campaignId) {
      throw new Error('abandonDepartureAudio requires campaignId');
    }
    if (!Number.isInteger(throughTurn) || throughTurn < 0) {
      throw new Error('abandonDepartureAudio requires a non-negative throughTurn');
    }
    const url = `${this.baseUrl}/api/quests/depart/abandon-audio`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId, through_turn: throughTurn }),
    });
  }

  async chooseQuestDeparture(campaignId, choice, expectedVersion) {
    if (!campaignId) {
      throw new Error('chooseQuestDeparture requires campaignId');
    }
    if (!['return_to_jim', 'continue'].includes(choice)) {
      throw new Error('chooseQuestDeparture requires a valid choice');
    }
    if (!Number.isInteger(expectedVersion)) {
      throw new Error('chooseQuestDeparture requires expectedVersion');
    }
    const url = `${this.baseUrl}/api/quests/handoff/departure`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({
        campaign_id: campaignId,
        choice,
        expected_version: expectedVersion,
      }),
    });
  }

  /**
   * Return from the quest board to Jim without selecting a quest.
   * Backed by POST /api/quests/board/close.
   */
  async closeQuestBoard(campaignId) {
    if (!campaignId) {
      throw new Error('closeQuestBoard requires campaignId');
    }
    const url = `${this.baseUrl}/api/quests/board/close`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  /**
   * Record the player's quest selection from the board.
   * Backed by POST /api/quests/board/select.
   */
  async selectQuest(campaignId, questId) {
    if (!campaignId) {
      throw new Error('selectQuest requires campaignId');
    }
    if (!questId) {
      throw new Error('selectQuest requires questId');
    }
    const url = `${this.baseUrl}/api/quests/board/select`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId, quest_id: questId }),
    });
  }

  /**
   * Select the base card to refine during the Soul Forge interlude.
   * Backed by POST /api/quests/soul-forge/select-card.
   */
  async selectSoulForgeBaseCard(campaignId, baseCardId) {
    if (!campaignId) {
      throw new Error('selectSoulForgeBaseCard requires campaignId');
    }
    if (!baseCardId) {
      throw new Error('selectSoulForgeBaseCard requires baseCardId');
    }
    const url = `${this.baseUrl}/api/quests/soul-forge/select-card`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId, base_card_id: baseCardId }),
    });
  }

  /**
   * Dispatch the AI evolved-card art for a preview-time forge option.
   * Backed by POST /api/quests/soul-forge/preview-option. The picker can
   * call this when the player picks an option so the actual evolved face
   * generates while they read the stats, instead of only after commit.
   */
  async previewSoulForgeOption(campaignId, primitiveId, baseCardId = null) {
    if (!campaignId) {
      throw new Error('previewSoulForgeOption requires campaignId');
    }
    if (!primitiveId) {
      throw new Error('previewSoulForgeOption requires primitiveId');
    }
    const url = `${this.baseUrl}/api/quests/soul-forge/preview-option`;
    const body = { campaign_id: campaignId, primitive_id: primitiveId };
    if (baseCardId) body.base_card_id = baseCardId;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /**
   * Commit a Soul Forge option and return the refreshed quest view-state.
   * Backed by POST /api/quests/soul-forge/commit.
   */
  async commitSoulForgeSelection(campaignId, selection) {
    if (!campaignId) {
      throw new Error('commitSoulForgeSelection requires campaignId');
    }
    if (!selection || typeof selection !== 'object') {
      throw new Error('commitSoulForgeSelection requires selection');
    }
    const url = `${this.baseUrl}/api/quests/soul-forge/commit`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId, selection }),
    });
  }

  /**
   * Exit Soul Forge without consuming the artifact.
   * Backed by POST /api/quests/soul-forge/save-for-later.
   */
  async saveSoulForgeForLater(campaignId) {
    if (!campaignId) {
      throw new Error('saveSoulForgeForLater requires campaignId');
    }
    const url = `${this.baseUrl}/api/quests/soul-forge/save-for-later`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  /**
   * Exit the Soul Forge reveal and return to Jim.
   * Backed by POST /api/quests/soul-forge/return-to-jim.
   */
  async returnSoulForgeToJim(campaignId) {
    if (!campaignId) {
      throw new Error('returnSoulForgeToJim requires campaignId');
    }
    const url = `${this.baseUrl}/api/quests/soul-forge/return-to-jim`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId }),
    });
  }

  /**
   * Read-only narrow accessor for the Soul Forge interlude slice of the quest
   * view-state. Used by the dedicated interlude controller's art poll so it can
   * pick up the evolved-card face once the AI job completes WITHOUT churning the
   * whole quest-loop surface. No dedicated backend endpoint — the soul-forge
   * payload is already carried in /api/quests/view-state (BOU-1681).
   */
  async getSoulForgeData(campaignId) {
    if (!campaignId) {
      throw new Error('getSoulForgeData requires campaignId');
    }
    const viewState = await this.getQuestViewState(campaignId);
    return viewState?.soul_forge_interlude?.payload ?? null;
  }

  /**
   * Advance the current cutscene — backend will move to transition_target_phase
   * and return the refreshed QuestViewStateResponse.
   * Backed by POST /api/quests/cutscene/advance.
   */
  async advanceQuestCutscene(campaignId, version) {
    if (!campaignId) {
      throw new Error('advanceQuestCutscene requires campaignId');
    }
    if (typeof version !== 'number') {
      throw new Error('advanceQuestCutscene requires numeric version');
    }
    const url = `${this.baseUrl}/api/quests/cutscene/advance`;
    return this._fetchJsonWithDedupe(url, {
      method: 'POST',
      body: JSON.stringify({ campaign_id: campaignId, version }),
    });
  }

  // ==========================================================================
  // Campaign Recap API
  // ==========================================================================

  /**
   * Get the latest campaign recap (narrative prose + cast of characters)
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} { success, recap }
   */
  async getCampaignRecap(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${this.encodePathSegment(campaignId)}/recap`
    );
  }

  /**
   * Get the player journal (structured knowledge grouped by section)
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<Object>} { success, journal }
   */
  async getPlayerJournal(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/campaigns/${this.encodePathSegment(campaignId)}/journal`
    );
  }

  // ==========================================================================
  // Interactive Stage API
  // ==========================================================================

  /**
   * Fetch the interactive-stage descriptor for a campaign (player surface).
   * @param {string} campaignId - Campaign/session ID
   * @param {string|number} [cacheBust] - optional cache-bust token. When set, it
   *   is appended as a throwaway query param so a FORCED refresh (e.g. after a
   *   scene-object socket event or a same-campaign location move) doesn't collapse
   *   onto an older in-flight GET for the same URL inside `_fetchJsonWithDedupe`.
   *   Unknown query params are ignored server-side.
   * @returns {Promise<Object>} Interactive stage descriptor
   */
  async getInteractiveStage(campaignId, cacheBust) {
    let url = `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}/interactive-stage`;
    if (cacheBust !== undefined && cacheBust !== null && cacheBust !== '') {
      url += `${url.includes('?') ? '&' : '?'}_r=${encodeURIComponent(cacheBust)}`;
    }
    return this._fetchJsonWithDedupe(url);
  }

  /**
   * Fetch the authored action autoplay would submit, served ONLY on the
   * explicit autoplay request (BOU-2977 review). Unlike the interactive-stage
   * descriptor this may be the beat's guidance rung — the player has already
   * pressed the "do it for me" quadrant.
   * @param {string} campaignId - Campaign/session ID
   * @returns {Promise<{object_id:string,object_name:string,action_text:string}>}
   *   Rejects (404) when no actionable gate resolves.
   */
  async getAutoplayAction(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/scenes/campaign/${this.encodePathSegment(campaignId)}/autoplay-action`,
      {},
      // 404 is this route's DESIGNED answer for "no actionable gate resolves", and
      // the shared helper's catch-all retries any error. Retrying a deliberate 404
      // costs three requests and ~3s before the ring can show its empty state
      // (BOU-3136), so this call takes the answer the first time.
      { maxAttempts: 1 }
    );
  }

  /**
   * List every location for a campaign (admin surface). Sourced straight from
   * game.locations so no location is hidden behind the campaign detail's
   * truncated scene-history window.
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array<{location_id:string,name:string,location_type:string,has_stage_background:boolean}>>}
   */
  async getAdminCampaignLocations(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/admin/locations?campaign_id=${encodeURIComponent(campaignId)}`
    );
  }

  /**
   * Fetch the interactive-stage descriptor for a location (admin surface).
   * @param {string} locationId - Location ID
   * @param {string|number} [cacheBust] - optional cache-bust token. When set, it
   *   is appended as a throwaway query param so an imperative refresh after a
   *   state-change POST (or the Lab poll) doesn't collapse onto an older in-flight
   *   GET for the same location URL inside `_fetchJsonWithDedupe` and commit the
   *   stale pre-POST `current_state`. Mirrors `getInteractiveStage`; unknown query
   *   params are ignored server-side.
   * @returns {Promise<Object>} Interactive stage descriptor
   */
  async getAdminInteractiveStage(locationId, cacheBust, campaignId) {
    let url = `${this.baseUrl}/api/admin/locations/${this.encodePathSegment(locationId)}/interactive-stage`;
    if (campaignId) {
      // Shared/global stages (Location.campaign_id NULL) require the campaign
      // context to resolve SceneObjectCampaignState overlays — same contract
      // as the localize/cutout/state admin calls (finding 3537765498).
      url += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    if (cacheBust !== undefined && cacheBust !== null && cacheBust !== '') {
      url += `${url.includes('?') ? '&' : '?'}_r=${encodeURIComponent(cacheBust)}`;
    }
    return this._fetchJsonWithDedupe(url);
  }

  /**
   * Fetch one object's authored state machine with live campaign guard verdicts.
   */
  async getAdminSceneObjectFlow(locationId, objectId, campaignId, cacheBust) {
    let url = `${this.baseUrl}/api/admin/locations/${this.encodePathSegment(locationId)}/interactive-stage/objects/${this.encodePathSegment(objectId)}/flow`;
    const params = [];
    if (campaignId) {
      params.push(`campaign_id=${encodeURIComponent(campaignId)}`);
    }
    // `_fetchJsonWithDedupe` keys on method+url+body, so a re-read issued right
    // after a state-change POST would be handed the PRE-POST promise still in
    // flight and commit stale verdicts, with no later dependency change to
    // correct them. A unique token per refresh gives it its own key. Mirrors the
    // identical fix on the admin stage descriptor (useInteractiveStage.js).
    if (cacheBust) {
      params.push(`_cb=${encodeURIComponent(cacheBust)}`);
    }
    if (params.length) {
      url += `?${params.join('&')}`;
    }
    return this._fetchJsonWithDedupe(url);
  }

  /**
   * Kick off localization of a location's interactive stage (admin surface).
   * @param {string} locationId - Location ID
   * @param {string} [campaignId] - optional campaign context. Shared/global
   *   stages (Location.campaign_id NULL, e.g. the canonical Gilded Rat) have no
   *   resolvable campaign of their own, so the backend returns `not_queued` with
   *   no job unless the caller passes the campaign the stage is being localized
   *   for. Threaded as `?campaign_id=` (finding 3537765498).
   * @returns {Promise<Object>} Localize job result
   */
  async postAdminInteractiveStageLocalize(locationId, campaignId) {
    let path = `/api/admin/locations/${this.encodePathSegment(locationId)}/interactive-stage/localize`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this.makeRequest(path, null, 'POST');
  }

  /**
   * Generate cutouts for a scene object (admin surface).
   * @param {string} locationId - Location ID
   * @param {string} objectId - Scene object ID
   * @param {string} [campaignId] - optional campaign context for objects on a
   *   shared/global stage whose own campaign_id is NULL; without it the backend
   *   drops the request as `skipped_canonical` (finding 3537765498).
   * @returns {Promise<Object>} Cutout job result
   */
  async postAdminSceneObjectCutouts(locationId, objectId, campaignId) {
    let path = `/api/admin/locations/${this.encodePathSegment(locationId)}/interactive-stage/objects/${this.encodePathSegment(objectId)}/cutouts`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this.makeRequest(path, null, 'POST');
  }

  /**
   * Set the state of a scene object (admin surface).
   * @param {string} locationId - Location ID
   * @param {string} objectId - Scene object ID
   * @param {string} state - New object state
   * @param {string} [campaignId] - optional campaign context for shared/global
   *   stages whose objects do not carry their own campaign_id; without it the
   *   backend cannot broadcast the live state change to campaign watchers.
   * @returns {Promise<Object>} Updated object payload
   */
  async postAdminSceneObjectState(locationId, objectId, state, campaignId) {
    let path = `/api/admin/locations/${this.encodePathSegment(locationId)}/interactive-stage/objects/${this.encodePathSegment(objectId)}/state`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this.makeRequest(
      path,
      { state },
      'POST'
    );
  }

  /**
   * Fetch a location's persisted character-placement measurement (admin surface).
   * @param {string} locationId - Location ID
   * @param {string} [campaignId] - optional campaign context for shared/global stages
   * @returns {Promise<Object>} { placements, plate_url, plate_width, plate_height, stale }
   */
  async getAdminCharacterPlacements(locationId, campaignId) {
    // baseUrl-prefixed like every other _fetchJsonWithDedupe caller in this
    // file. A bare root-relative path only works while the API is same-origin
    // (true behind the dev proxy, false wherever the API has its own host), so
    // it would have passed locally and 404'd on a deployed frontend.
    let path = `${this.baseUrl}/api/admin/locations/${this.encodePathSegment(locationId)}/character-placements`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this._fetchJsonWithDedupe(path);
  }

  /**
   * Run (or skip-if-fresh) a character-placement measurement pass (admin surface).
   * @param {string} locationId - Location ID
   * @param {string} [campaignId] - optional campaign context for shared/global stages
   * @param {boolean} replace - true forces a fresh pass; false reuses the stored
   *   payload when it already matches the current plate
   * @returns {Promise<Object>} The measured LocationCharacterPlacements payload
   */
  async postAdminCharacterPlacementsMeasure(locationId, campaignId, replace) {
    let path = `/api/admin/locations/${this.encodePathSegment(locationId)}/character-placements/measure`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this.makeRequest(path, { replace }, 'POST');
  }

  /**
   * Run a character-placement SAMPLING pass (image-model; admin surface).
   * @param {string} locationId - Location ID
   * @param {string} [campaignId] - optional campaign context for shared/global stages
   * @param {number} rounds - generate+locate rounds to pool (default 2)
   * @param {boolean} persist - true writes the sampled payload; false only computes
   * @returns {Promise<Object>} The computed LocationCharacterPlacements payload
   */
  async postAdminCharacterPlacementsSample(locationId, campaignId, rounds, persist) {
    let path = `/api/admin/locations/${this.encodePathSegment(locationId)}/character-placements/sample`;
    if (campaignId) {
      path += `?campaign_id=${encodeURIComponent(campaignId)}`;
    }
    return this.makeRequest(path, { rounds, persist }, 'POST');
  }

  /**
   * List the placeable character cutouts for a campaign (admin surface).
   * @param {string} campaignId - Campaign ID
   * @returns {Promise<Array<{character_id:string,name:string,cutout_url:string|null,foot_point_y:number|null}>>}
   */
  async getAdminCharacterCutouts(campaignId) {
    return this._fetchJsonWithDedupe(
      `${this.baseUrl}/api/admin/locations/character-placement/cutouts?campaign_id=${encodeURIComponent(campaignId)}`
    );
  }

  async reportContent({ contentType, contentId, sessionId, reason, description }) {
    return this.makeRequest('/api/reports/content', {
      content_type: contentType,
      content_id: contentId,
      session_id: sessionId,
      reason,
      description: description || null,
    });
  }
}

// Create and export singleton instance
export const apiService = new ApiService();
export default apiService;
